import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneIncoming, Phone, PhoneOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CallLineView } from '@/services/dialer/callPage/lines';
import { LineBadge } from './LineBadge';
import { formatClock } from './time';

interface Props {
  lines: CallLineView[];
  audibleId: string | null;
  missedInbound: number;
  now: number;
  compact: boolean;
  onConsult(line: CallLineView): void;
  /** Spec §4.4: an inbound call outside a conversation, never answered by itself. */
  incoming?: { name: string; phone: string } | null;
  onAccept?: () => void;
  onReject?: () => void;
}

/**
 * Spec §4.1 / §7.2: who rings, for how long. Status changes are announced
 * politely; the timers are not (spec §4.6). Under 1280 px it becomes a
 * strip of pills under the session bar.
 */
export function LinesColumn({ lines, audibleId, missedInbound, now, compact, onConsult, incoming, onAccept, onReject }: Props) {
  const { t } = useTranslation(['dialer']);
  // Announce only the lines whose state changed, not the whole column.
  const previous = useRef(new Map<string, string>());
  const [announce, setAnnounce] = useState('');
  useEffect(() => {
    const changed = lines.filter((l) => previous.current.get(l.parallelCallId) !== l.state);
    previous.current = new Map(lines.map((l) => [l.parallelCallId, l.state]));
    if (changed.length) {
      setAnnounce(changed.map((l) => t('dialer:callPage.lines.announce', { name: l.displayName, state: t(`dialer:callPage.lines.state.${l.state}`) })).join('. '));
    }
  }, [lines, t]);

  const items = lines.map((line) => {
    const timer = ['dialing', 'ringing', 'connected'].includes(line.state) ? formatClock(now - line.since) : null;
    const isAudible = line.parallelCallId === audibleId;
    return (
      <li key={line.parallelCallId} className={compact ? 'min-w-[180px] shrink-0' : undefined}>
        <button
          type="button"
          onClick={() => onConsult(line)}
          aria-current={isAudible ? 'true' : undefined}
          className={`w-full rounded-lg border text-left transition-colors focus-visible:outline focus-visible:outline-2 ${compact ? 'px-2 py-1' : 'px-3 py-2'}`}
          style={{
            borderColor: isAudible ? 'var(--vf-status-connected-fg)' : 'var(--vf-border)',
            background: 'var(--vf-surface-1)',
            outlineColor: 'var(--vf-accent)',
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-medium" style={{ color: 'var(--vf-text-1)' }}>{line.displayName}</span>
            {timer && <span className="text-xs tabular-nums" style={{ color: 'var(--vf-text-2)' }}>{timer}</span>}
          </div>
          <div className="mt-1"><LineBadge state={line.state} /></div>
          {line.callBackFirst && !compact && (
            <p className="mt-1 text-[11px] font-medium" style={{ color: 'var(--vf-status-callback-fg)' }}>
              {t('dialer:callPage.lines.callBackFirst')}
            </p>
          )}
        </button>
      </li>
    );
  });

  return (
    <section
      aria-label={t('dialer:callPage.lines.title')}
      className={compact ? 'border-b px-3 py-2' : 'flex w-[280px] shrink-0 flex-col border-r p-3'}
      style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' }}
    >
      {!compact && <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--vf-text-2)' }}>{t('dialer:callPage.lines.title')}</h2>}
      <div className="sr-only" aria-live="polite">{announce}</div>
      {incoming && (
        <div
          role="group"
          aria-label={t('dialer:callPage.lines.incoming', { name: incoming.name || incoming.phone })}
          className="mb-3 rounded-lg border p-3"
          style={{ borderColor: 'var(--vf-status-ringing-fg)', background: 'var(--vf-surface-1)' }}
        >
          <p className="flex items-center gap-1.5 text-xs font-medium" style={{ color: 'var(--vf-status-ringing-fg)' }}>
            <PhoneIncoming className="h-3.5 w-3.5 vf-pulse" aria-hidden />{t('dialer:callPage.lines.incomingLabel')}
          </p>
          <p className="mt-1 truncate text-sm font-medium" style={{ color: 'var(--vf-text-1)' }}>{incoming.name || incoming.phone}</p>
          {incoming.name && <p className="text-xs tabular-nums" style={{ color: 'var(--vf-text-2)' }}>{incoming.phone}</p>}
          <div className="mt-2 flex gap-2">
            <Button size="sm" className="min-h-11 flex-1 bg-[var(--vf-fill-success)] text-[var(--vf-primary-fg)] hover:brightness-110" onClick={onAccept}>
              <Phone className="mr-1 h-4 w-4" aria-hidden />{t('dialer:callPage.lines.accept')}
            </Button>
            <Button size="sm" variant="outline" className="min-h-11 flex-1" onClick={onReject}>
              <PhoneOff className="mr-1 h-4 w-4" aria-hidden />{t('dialer:callPage.lines.reject')}
            </Button>
          </div>
        </div>
      )}
      {lines.length === 0
        ? <p className="text-sm" style={{ color: 'var(--vf-text-3)' }}>{t('dialer:callPage.lines.empty')}</p>
        : <ul className={compact ? 'flex gap-2 overflow-x-auto' : 'flex flex-col gap-2 overflow-y-auto'}>{items}</ul>}
      {missedInbound > 0 && (
        <p className="mt-3 flex items-center gap-1.5 text-xs" style={{ color: 'var(--vf-text-2)' }}>
          <PhoneIncoming className="h-3.5 w-3.5" aria-hidden />
          {t('dialer:callPage.lines.missedInbound', { count: missedInbound })}
        </p>
      )}
    </section>
  );
}
